/*!
 * liveClockLink.js — Oscilla Live on a local install, played solo, keeps Link's beat
 * © 2026 Rob Canning — GPLv3
 *
 * Oscilla Live's only connection is a room's (roomLink.js). Played alone,
 * on a page Oscilla's server serves, it would have none, and Ableton Link
 * (the server's --link, server/linkBridge.js) could not reach it. So with
 * no room it joins the server's clock alone: clock_join at the page's own
 * /live-relay (server/liveRelay.js). Its pings set the shared clock
 * (serverClock.js), Link's timelines come as a room's would (roomBeat.js),
 * and this console's tempo() and downbeat() go to Link (useClockLink).
 *
 * Asked once, quietly: a server without --link, or a static host with no
 * Oscilla server behind it, refuses or does not answer, and nothing more is
 * tried. Once joined, a dropped connection (the server restarted) is joined
 * again. A room comes first: joining one closes this, since the room
 * carries Link's beat on its own server; leaving it opens this again.
 */

import { startServerClock, stopServerClock, onTimePong } from "../system/serverClock.js";
import { deliverRoomMessage } from "../system/roomEvents.js";
import { useClockLink, askRoomTimeline } from "../cues/audio/roomBeat.js";

const RETRY_FIRST_MS = 1000;
const RETRY_MOST_MS = 15000;

/** The page's own /live-relay, ws:// or wss:// as the page is. */
export function ownRelay(loc = location) {
  return `${loc.protocol === "https:" ? "wss" : "ws"}://${loc.host}/live-relay`;
}

/**
 * Join the clock alone, when there is no room. Returns { stop() }.
 * @param {{ address?: string, log?: (msg: string) => void }} [o]
 */
export function startClockLink({ address = ownRelay(), log = msg => console.info(msg) } = {}) {
  let ws = null, timer = null;
  let given = false;           // the server has refused, or is not there: no more tries
  let joinedOnce = false;      // joined before: a drop is tried again
  let inRoom = false;
  let retryMs = RETRY_FIRST_MS;

  function connect() {
    if (given || inRoom || ws) return;
    let me;
    try { me = new WebSocket(address); } catch { given = true; return; }
    ws = me;
    me.addEventListener("open", () => me.send(JSON.stringify({ type: "clock_join" })));
    me.addEventListener("message", (e) => {
      let d;
      try { d = JSON.parse(e.data); } catch { return; }
      switch (d?.type) {
        case "clock_joined":
          joinedOnce = true; retryMs = RETRY_FIRST_MS;
          startServerClock(me);
          useClockLink({ send: m => { if (me.readyState === WebSocket.OPEN) me.send(JSON.stringify(m)); } });
          askRoomTimeline();
          log("[clock] joined this server's clock: Ableton Link's beat");
          return;
        case "join_refused":
          given = true;          // no Link here: quietly, once
          return;
        case "time_pong":
          onTimePong(d);
          return;
        default:
          deliverRoomMessage(d); // livecode_timeline: Link's beat
      }
    });
    me.addEventListener("close", () => {
      if (ws !== me) return;
      ws = null;
      useClockLink(null);
      if (!inRoom) stopServerClock();
      if (!joinedOnce) given = true;   // never joined: no Oscilla server with Link here
      if (given || inRoom) return;
      timer = setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, RETRY_MOST_MS);
    });
  }

  function pause() {
    inRoom = true;
    clearTimeout(timer);
    useClockLink(null);
    const old = ws; ws = null;
    old?.close();                // the room's socket has the clock now: not stopped here
  }
  function resume() {
    inRoom = false;
    retryMs = RETRY_FIRST_MS;
    connect();
  }

  window.addEventListener("oscilla:room-open", pause);
  window.addEventListener("oscilla:room-closed", resume);
  connect();
  return {
    stop() {
      given = true;
      clearTimeout(timer);
      window.removeEventListener("oscilla:room-open", pause);
      window.removeEventListener("oscilla:room-closed", resume);
      useClockLink(null);
      ws?.close();
    },
  };
}
